import { Field, FieldModel } from '@cibo/core'
import {
  FieldSearchBar,
  PaginatedFieldsProvider,
  fieldNameColumn,
  usePaginatedFields,
} from '@cibo/landmanager'
import { Loading } from '@cibo/profile'
import { useProgramModel, useUpdateProgramEngagement } from '@cibo/programs'
import { TaskComponentProps } from '@cibo/programs/src/features/Tasks/types'
import { DataGridPro, logRequestError } from '@cibo/ui'
import { useSaveIndicator } from '@cibo/ui/src/components/SaveIndicator/SaveIndicatorProvider'
import { Stack, Typography } from '@mui/material'
import {
  GridColDef,
  GridInputSelectionModel,
  GridRenderCellParams,
  GridRowParams,
} from '@mui/x-data-grid-pro'
import { useSnackbar } from 'notistack'
import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Markdown, Section } from '../../consts/Theme'

type SelectFieldsProps = Omit<TaskComponentProps, 'onUpdateFields'>

const SelectFieldsTable = ({ engagement, task, userRole }: SelectFieldsProps) => {
  const { t } = useTranslation('@cibo/impact/SelectFields')
  const [selectedRowIds, setSelectedRowIds] = useState<Field['resourceId'][]>(engagement.fields)
  const programModel = useProgramModel({ programId: engagement.programId })
  const updateEngagement = useUpdateProgramEngagement()
  const { enqueueSnackbar, closeSnackbar } = useSnackbar()
  const nameColumn = fieldNameColumn({ t })
  const { setIsDirty, setIsError, setIsSaving } = useSaveIndicator()
  const taskStatus = engagement.getTaskStatus(task.id)
  const fields = usePaginatedFields(0, 1000, { owner: engagement.ownedBy?.userId })

  const columns = useMemo(
    () =>
      [
        nameColumn,
        {
          field: 'programStatus',
          headerName: '',
          flex: 1,
          sortable: false,
          filterable: false,
          //@todo update design
          renderCell: (params: GridRenderCellParams<string, FieldModel>) => {
            const programStatus = params.row.getProgramStatus(engagement.programId)
            return (
              !programStatus?.availableForWorkflow &&
              programStatus?.workflow !== engagement.id && (
                <Typography variant="body2">{t('notAvailableForWorkflow')}</Typography>
              )
            )
          },
        },
      ] as GridColDef[],
    []
  )

  const taskContent = programModel.data?.taskContent(task.id)

  const onSelectionModelChange = (selectionModel: GridInputSelectionModel) => {
    const newIds = selectionModel as string[]
    if (newIds.join() !== engagement.fields.join()) {
      closeSnackbar()
      setIsDirty(true)
      setIsSaving(true)
      setIsError(false)
      setSelectedRowIds(newIds)
      const clear = newIds.length === 0
      updateEngagement
        .mutateAsync({
          id: engagement.id,
          /**
           * this darling little bit of code is needed to clear fields
           * because apparently `replace: []` is a noop on the backend
           */
          fields: clear ? engagement.fields : newIds,
          action: clear ? 'remove' : 'replace',
        })
        .then(() => {
          setIsSaving(false)
        })
        .catch(err => {
          logRequestError(err, {
            engagementId: engagement.id,
            fields: clear ? engagement.fields : newIds,
            action: clear ? 'remove' : 'replace',
          })
          setIsSaving(false)
          setIsError(true)
          enqueueSnackbar(t(`error_${err.response?.data.errMsgKey}`), {
            variant: 'error',
          })
        })
    }
  }

  const isRowSelectable = useCallback(
    (params: GridRowParams<FieldModel>) => {
      const programStatus = params.row.getProgramStatus(engagement.programId)
      return (
        !!taskStatus?.allowedActions?.includes('canModify') &&
        (programStatus?.availableForWorkflow || programStatus?.workflow === engagement.id)
      )
    },
    [taskStatus?.allowedActions?.includes('canModify')]
  )

  const getRowId = useCallback((row: FieldModel) => row.resourceId, [])

  if (programModel.isLoading || fields.isLoading) {
    return <Loading />
  }

  return (
    <Stack spacing={3} sx={{ flex: 1 }}>
      <Section>
        <Stack padding={3} spacing={3}>
          <Markdown typographyProps={{ lineHeight: 1.5, variant: 'body2' }}>
            {taskContent?.content?.markdown?.[userRole]?.replaceAll(
              '{{name}}',
              engagement.ownedBy.givenName ?? ''
            )}
          </Markdown>
          <FieldSearchBar />
          <DataGridPro
            columns={columns}
            getRowHeight={() => 'auto'}
            isRowSelectable={isRowSelectable}
            loading={fields.isFetching}
            pageSize={10}
            rows={fields.data?.items ?? []}
            selectionModel={selectedRowIds}
            getRowId={getRowId}
            onSelectionModelChange={onSelectionModelChange}
            checkboxSelection
            autoHeight
            hideCellFocus
            pagination
          />
        </Stack>
      </Section>
    </Stack>
  )
}

export const SelectFields = (params: SelectFieldsProps) => {
  return (
    <PaginatedFieldsProvider>
      <SelectFieldsTable {...params} />
    </PaginatedFieldsProvider>
  )
}
